// src/components/admin/SisterGroupModal.jsx
// -----------------------------------------
// db-205 editor: put a DFV Service into an existing sister group, a new one, or
// none. A SINGLE ATOMIC ACTION with its own Save (not part of the Details form, so
// it never touches the shelter save path or the Unleashed push). The save steps
// (deactivate old -> create group if new -> add) live in services/sisterGroups.ts.
// 7 Oct 2026: when the service is in a group, a "Manage group" section can RENAME the
// group (an active name already in use is refused) or RETIRE it for good (in-window
// confirm; every membership is switched off first — see data/sisterGroups.ts).

import React from "react";
import {
  addSisterMember,
  createSisterGroup,
  deactivateSisterMember,
  renameSisterGroup,
  retireSisterGroup,
} from "../../data/sisterGroups";
import { saveShelterSisterGroup } from "../../services/sisterGroups";
import { ModalBackdrop } from "../form/FormUI.jsx";

const labelCls = "text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-2 block";

export function SisterGroupModal({ shelterId, shelterCode, entry, groups, onClose, onSaved }) {
  const groupList = Array.isArray(groups) ? groups : [];
  const [mode, setMode] = React.useState(
    entry ? "existing" : groupList.length ? "existing" : "new"
  );
  const [groupId, setGroupId] = React.useState(entry?.groupId || groupList[0]?.id || "");
  const [newName, setNewName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [renameTo, setRenameTo] = React.useState(entry?.groupName ?? "");
  const [confirmRetire, setConfirmRetire] = React.useState(false);

  async function rename() {
    if (busy || !entry) return;
    setErr("");
    const trimmed = renameTo.trim();
    if (!trimmed) {
      setErr("A group name is required.");
      return;
    }
    const clash = groupList.some(
      (g) => g.id !== entry.groupId && (g.name ?? "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (clash) {
      setErr(`Another group is already called "${trimmed}".`);
      return;
    }
    setBusy(true);
    const { error } = await renameSisterGroup(entry.groupId, trimmed);
    setBusy(false);
    if (error) {
      setErr(error.message || "Could not rename the group.");
      return;
    }
    onSaved();
  }

  async function retire() {
    if (busy || !entry) return;
    setErr("");
    setBusy(true);
    const { error } = await retireSisterGroup(entry.groupId);
    setBusy(false);
    if (error) {
      setErr(error.message || "Could not retire the group.");
      return;
    }
    onSaved();
  }

  async function save() {
    if (busy) return;
    setErr("");
    const choice = mode === "none" ? { none: true } : mode === "new" ? { newName } : { groupId };
    setBusy(true);
    const { error } = await saveShelterSisterGroup(
      {
        shelterId,
        currentMemberId: entry?.memberId ?? null,
        currentGroupId: entry?.groupId ?? null,
        choice,
      },
      { addSisterMember, createSisterGroup, deactivateSisterMember }
    );
    setBusy(false);
    if (error) {
      setErr(error.message || "Could not save the sister group.");
      return;
    }
    onSaved();
  }

  const otherNames = (entry?.sisters ?? []).map((s) => `${s.code} ${s.name}`);
  const memberCount = (entry?.sisters?.length ?? 0) + 1;
  const memberCountText = memberCount === 1 ? "This service" : `All ${memberCount} services`;

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle={`Sister group — ${shelterCode || ""}`}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 560,
        overflow: "hidden",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      <div className="p-5 text-[14px] text-zephyr-text">
        <span className={labelCls}>This service belongs to</span>

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input
            type="radio"
            name="sister-mode"
            checked={mode === "existing"}
            disabled={groupList.length === 0}
            onChange={() => setMode("existing")}
          />
          <span>An existing group</span>
        </label>
        {mode === "existing" && (
          <div className="ml-6 mb-3">
            <label htmlFor="sister-existing" className="sr-only">
              Existing group
            </label>
            <select
              id="sister-existing"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full max-w-[420px] py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] bg-white"
            >
              {groupList.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input
            type="radio"
            name="sister-mode"
            checked={mode === "new"}
            onChange={() => setMode("new")}
          />
          <span>A new group named</span>
        </label>
        {mode === "new" && (
          <div className="ml-6 mb-3">
            <label htmlFor="sister-new-name" className="sr-only">
              New group name
            </label>
            <input
              id="sister-new-name"
              type="text"
              value={newName}
              maxLength={120}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Nova for Women and Children"
              className="w-full max-w-[420px] py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px]"
            />
          </div>
        )}

        {entry && (
          <label className="flex items-start gap-2 mb-3 cursor-pointer">
            <input
              type="radio"
              name="sister-mode"
              checked={mode === "none"}
              onChange={() => setMode("none")}
            />
            <span>No group (remove from sister group)</span>
          </label>
        )}

        {otherNames.length > 0 && (
          <div className="mt-4">
            <span className={labelCls}>Other services in this group</span>
            <div className="text-[13px] text-zephyr-muted">{otherNames.join(" · ")}</div>
          </div>
        )}

        {entry && (
          <div className="mt-5 pt-4 border-t border-zephyr-border">
            <span className={labelCls}>Manage group "{entry.groupName}"</span>
            <div className="flex items-center gap-2 flex-wrap">
              <label htmlFor="sister-rename" className="sr-only">
                Rename group
              </label>
              <input
                id="sister-rename"
                type="text"
                value={renameTo}
                maxLength={120}
                onChange={(e) => setRenameTo(e.target.value)}
                className="flex-1 min-w-[200px] max-w-[320px] py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px]"
              />
              <button
                type="button"
                onClick={rename}
                disabled={busy || renameTo.trim() === (entry.groupName ?? "").trim()}
                className="rounded-[8px] py-[7px] px-4 text-[13px] border border-zephyr-border bg-white text-zephyr-text disabled:opacity-50"
              >
                Rename
              </button>
            </div>
            {!confirmRetire ? (
              <button
                type="button"
                onClick={() => setConfirmRetire(true)}
                disabled={busy}
                className="mt-3 rounded-[8px] py-[7px] px-4 text-[13px] font-semibold border border-zephyr-red bg-white text-zephyr-red disabled:opacity-50"
              >
                Retire group
              </button>
            ) : (
              <div className="mt-3 rounded-[8px] bg-zephyr-red-light py-3 px-3 text-[13px]">
                <div className="mb-2">
                  Retire "{entry.groupName}"? {memberCountText} will be removed from it. This can't
                  be undone in the portal.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={retire}
                    disabled={busy}
                    className="rounded-[8px] py-[6px] px-4 text-[13px] font-semibold text-white bg-zephyr-red disabled:opacity-50"
                  >
                    {busy ? "Retiring…" : "Yes, retire"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRetire(false)}
                    disabled={busy}
                    className="rounded-[8px] py-[6px] px-4 text-[13px] border border-zephyr-border bg-white text-zephyr-text"
                  >
                    Keep group
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 text-[12px] text-zephyr-muted">
          Saved on its own — does not touch the Details form or Unleashed. Shelters never see sister
          groups.
        </div>

        {err && (
          <div className="mt-3 text-[13px] text-zephyr-red bg-zephyr-red-light rounded-[8px] py-2 px-3">
            {err}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center py-3 px-5 border-t border-zephyr-border">
        <button
          type="button"
          onClick={onClose}
          className="rounded-[8px] py-[7px] px-4 text-[13px] border border-zephyr-border bg-white text-zephyr-text"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-[8px] py-[7px] px-4 text-[13px] font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--color-zephyr-teal-dark)" }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalBackdrop>
  );
}
